import { useState } from 'react'
import nudgLogo from '@/assets/branding/nudg-u-logo.png'

function AuthPage({ onSignIn, onSignUp, onResetPassword, passwordRecovery, onUpdatePassword, onPasswordUpdateComplete }) {
  const [mode, setMode] = useState('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [signUpSuccess, setSignUpSuccess] = useState(false)
  const [resetSent, setResetSent] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      if (mode === 'reset') {
        if (!email.trim()) return
        const { error } = await onResetPassword(email.trim())
        if (error) throw error
        setResetSent(true)
      } else if (mode === 'signup') {
        if (!email.trim() || !password) return
        const { error } = await onSignUp(email.trim(), password)
        if (error) throw error
        setSignUpSuccess(true)
      } else {
        if (!email.trim() || !password) return
        const { error } = await onSignIn(email.trim(), password)
        if (error) throw error
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handlePasswordUpdate(e) {
    e.preventDefault()
    setError(null)

    if (password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)
    try {
      const { error } = await onUpdatePassword(password)
      if (error) throw error
      onPasswordUpdateComplete()
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  function switchMode(newMode) {
    setMode(newMode)
    setError(null)
    setSignUpSuccess(false)
    setResetSent(false)
  }

  if (signUpSuccess) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <img className="auth-logo" src={nudgLogo} alt="" />
          <h1 className="auth-title">Nudg.U</h1>
          <div className="auth-success">
            <p>Account created! Check your email to confirm your account, then sign in.</p>
          </div>
          <button
            type="button"
            className="auth-toggle"
            onClick={() => switchMode('signin')}
          >
            Back to Sign In
          </button>
        </div>
      </div>
    )
  }

  if (resetSent) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <img className="auth-logo" src={nudgLogo} alt="" />
          <h1 className="auth-title">Nudg.U</h1>
          <div className="auth-success">
            <p>If an account exists for {email}, you'll receive a password reset link shortly. Check your email.</p>
          </div>
          <button
            type="button"
            className="auth-toggle"
            onClick={() => switchMode('signin')}
          >
            Back to Sign In
          </button>
        </div>
      </div>
    )
  }

  if (passwordRecovery) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <img className="auth-logo" src={nudgLogo} alt="" />
          <h1 className="auth-title">Nudg.U</h1>
          <p className="auth-subtitle">Set New Password</p>

          <form className="auth-form" onSubmit={handlePasswordUpdate}>
            <input
              type="password"
              className="auth-input"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              autoComplete="new-password"
              minLength={6}
            />
            <input
              type="password"
              className="auth-input"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              autoComplete="new-password"
              minLength={6}
            />
            {password && confirmPassword && password !== confirmPassword && (
              <p className="auth-hint">Passwords do not match</p>
            )}

            {error && <p className="auth-error">{error}</p>}

            <button type="submit" className="btn btn-primary auth-submit" disabled={loading || (confirmPassword && password !== confirmPassword)}>
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <img className="auth-logo" src={nudgLogo} alt="" />
        <h1 className="auth-title">Nudg.U</h1>
        <p className="auth-subtitle">Your Executive Function Coach</p>

        <form className="auth-form" onSubmit={handleSubmit}>
          <input
            type="email"
            className="auth-input"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            autoComplete="email"
          />
          {mode !== 'reset' && (
            <input
              type="password"
              className="auth-input"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              minLength={6}
            />
          )}

          {error && <p className="auth-error">{error}</p>}

          <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
            {loading
              ? (mode === 'reset' ? 'Sending...' : mode === 'signup' ? 'Creating account...' : 'Signing in...')
              : (mode === 'reset' ? 'Send Reset Link' : mode === 'signup' ? 'Create Account' : 'Sign In')
            }
          </button>
        </form>

        {mode === 'signin' && (
          <button type="button" className="auth-toggle" onClick={() => switchMode('reset')}>
            Forgot your password?
          </button>
        )}

        <button type="button" className="auth-toggle" onClick={() => switchMode(mode === 'signin' ? 'signup' : 'signin')}>
          {mode === 'signin'
            ? "Don't have an account? Sign up"
            : 'Already have an account? Sign in'
          }
        </button>
      </div>
    </div>
  )
}

export default AuthPage
